import { useState } from 'react'
import type { Workspace } from '../../context/WorkspaceContext'
import { setBudgetEnforcement } from '../../services/budgetService'
import { useNls } from '../../nls/nlsProvider'
import { isModuleEnabled } from '../../utils/workspaceModules'
import { Pressable } from '../Common/Pressable'

export interface BudgetEnforcementControlProps {
  workspace: Workspace
  canEdit: boolean
  onError?: (error: string | null) => void
}

export const BudgetEnforcementControl = ({
  workspace,
  canEdit,
  onError,
}: BudgetEnforcementControlProps) => {
  const { t } = useNls()
  const [isSaving, setIsSaving] = useState(false)

  if (!isModuleEnabled(workspace, 'budget')) {
    return null
  }

  const currentMode: 'warn' | 'block' =
    workspace.modules?.budgetEnforcement === 'block' ? 'block' : 'warn'

  const handleModeChange = async (nextMode: 'warn' | 'block') => {
    if (!canEdit || isSaving || nextMode === currentMode) return
    setIsSaving(true)
    onError?.(null)
    try {
      await setBudgetEnforcement(workspace.id, nextMode)
    } catch (err) {
      onError?.(
        err instanceof Error
          ? err.message
          : t('NLSAdminBudgetEnforcementSaveFailed')
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="budgetEnforcementControl">
      <span className="budgetEnforcementControl__label">
        {t('NLSAdminBudgetEnforcementLabel')}
      </span>
      <div
        className="settingsSegmented budgetEnforcementControl__segmented"
        role="group"
        aria-label={t('NLSAdminBudgetEnforcementLabel')}
      >
        <Pressable
          className={currentMode === 'warn' ? 'isActive' : ''}
          aria-pressed={currentMode === 'warn'}
          disabled={!canEdit || isSaving}
          onClick={() => void handleModeChange('warn')}
        >
          {t('NLSAdminBudgetEnforcementWarn')}
        </Pressable>
        <Pressable
          className={currentMode === 'block' ? 'isActive' : ''}
          aria-pressed={currentMode === 'block'}
          disabled={!canEdit || isSaving}
          onClick={() => void handleModeChange('block')}
        >
          {t('NLSAdminBudgetEnforcementBlock')}
        </Pressable>
      </div>
      <p className="budgetEnforcementControl__hint">
        {t('NLSAdminBudgetEnforcementHint')}
      </p>
    </div>
  )
}
